import DateTimeRangeFilter from 'Components/Generic/DateTimeFilter'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { convertDateToRomeTimeZone } from 'Helpers/DateHelpers'
import { renderTranslation } from 'Helpers/GenericHelpers'
import { IColumn } from 'Interfaces/IColumn'
import {
  IOmnichannelLogItem,
  getOmnichannelLogs as getOmnichannelLog,
} from 'Services/Omnichannel/OmnichannelLogService'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { Button, Checkbox, Col, Input, Modal, Row, Select, Table } from 'antd'
import { useAppSelector } from 'hooks'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useStore } from 'react-redux'
import dayjs from 'dayjs'

export interface IOmnichannelLogsProps extends PropsWithChildren {
  isVisible: boolean
  onCancel: () => void
}

export default function OmnichannelLogs(props: IOmnichannelLogsProps) {
  const seasonList: any[] = useAppSelector(state => state.present.seasonRule.seasonList)
  const sites = useAppSelector(state => state.present.configuration.sites)
  const stores = useAppSelector(state => state.present.configuration.stores)
  const marketplace = useAppSelector(state => state.present.configuration.marketplace)
  const language = useAppSelector(state => state.present.language.value)
  const store = useStore()
  const [columns, setColumns] = useState([])
  const [rows, setRows] = useState([])
  const [selectedSeasons, setSelectedSeasons] = useState<Array<number>>([])
  const [selectedTypeOfChannel, setSelectedTypeOfChannel] = useState(TypeOfChannel.Marketplace)
  const [selectedOperations, setSelectedOperations] = useState<Array<number>>([])
  const [showSubRules, setShowSubRules] = useState(true)
  const [name, setName] = useState('')
  const [startDateTime, setStartDateTime] = useState(null)
  const [endDateTime, setEndDateTime] = useState(null)
  const [statusSeason, setStatusSeason] = useState<'' | 'warning' | 'error'>('')

  const operations: { value: number; label: string }[] = [
    { value: 0, label: renderTranslation(language, TranslationKeys.insertsLog) },
    { value: 1, label: renderTranslation(language, TranslationKeys.changesLog) },
    { value: 2, label: renderTranslation(language, TranslationKeys.deleteLog) },
  ]

  const typesOfChannels = useMemo(
    () =>
      Object.values(TypeOfChannel)
        .filter(v => !isNaN(Number(v)))
        .filter(x => (!sites ? x !== TypeOfChannel.Site : true))
        .filter(x => (!stores ? x !== TypeOfChannel.Store : true))
        .filter(x => (!marketplace ? x !== TypeOfChannel.Marketplace : true))
        .map(value => ({
          value,
          label: renderTranslation(language, TranslationKeys['channelType' + TypeOfChannel[String(value)]]),
        })),
    [sites, stores, marketplace, language],
  )

  const getLogs = async () => {
    if (!selectedSeasons || selectedSeasons.length <= 0) {
      setStatusSeason('error')
      return
    }
    setStatusSeason('')
    const logs = await getOmnichannelLog(store, {
      seasonIds: selectedSeasons,
      typeOfChannel: selectedTypeOfChannel,
      showSubRules: showSubRules,
      operations: selectedOperations,
      name: name,
      from: startDateTime,
      to: endDateTime,
    })
    for (let i = 0; i < logs.length; i++) {
      const log = logs[i] as IOmnichannelLogItem & { key: number }
      log.key = i
      log.dateTime = convertDateToRomeTimeZone(log.dateTime).format('DD/MM/YYYY HH:mm:ss')
    }

    setRows(logs)
  }

  const clearLogs = () => {
    setSelectedSeasons(null)
    setRows([])
  }

  const mapColumns: () => IColumn[] = () => {
    return [
      { title: renderTranslation(language, TranslationKeys.logTableDate), dataIndex: 'dateTime', key: 'dateTime' },
      { title: renderTranslation(language, TranslationKeys.logTableName), dataIndex: 'ruleName', key: 'ruleName' },
      {
        title: renderTranslation(language, TranslationKeys.logTableSeason),
        dataIndex: 'seasonName',
        key: 'seasonName',
      },
      { title: renderTranslation(language, TranslationKeys.logTableField), dataIndex: 'field', key: 'field' },
      { title: renderTranslation(language, TranslationKeys.logTableOldValue), dataIndex: 'oldValue', key: 'oldValue' },
      { title: renderTranslation(language, TranslationKeys.logTableNewValue), dataIndex: 'newValue', key: 'newValue' },
      { title: renderTranslation(language, TranslationKeys.logTableUser), dataIndex: 'user', key: 'user' },
      {
        title: renderTranslation(language, TranslationKeys.logTableOperation),
        dataIndex: 'typeOfLogStr',
        key: 'typeOfLogStr',
      },
    ]
  }
  const verifyAndSetStartDate = startDate => {
    setStartDateTime(startDate)
  }

  const verifyAndSetEndDate = endDate => {
    const validendDate =
      endDate.valueOf() >= startDateTime.valueOf()
        ? endDate
        : startDateTime.valueOf() >= dayjs().valueOf()
        ? startDateTime.clone().add(1, 'd')
        : dayjs().add(1, 'd')
    setEndDateTime(validendDate)
  }
  useEffect(() => {
    setColumns(mapColumns())
  }, [])

  return (
    <Modal
      title={'Omnichannel logs'}
      open={props.isVisible}
      closable={true}
      onCancel={props.onCancel}
      width={'90%'}
      footer={null}
    >
      <Row gutter={[16, 16]}>
        <Col>
          <DateTimeRangeFilter
            className=''
            fullFormat={true}
            inColumn={false}
            onDateFromChange={verifyAndSetStartDate}
            onDateToChange={verifyAndSetEndDate}
            setShowTime={{
              minuteStep: 15,
            }}
            setModalFix={true}
            setOnOkFrom={verifyAndSetStartDate}
            setOnOkTo={verifyAndSetEndDate}
            setDateFrom={startDateTime}
            setDateTo={endDateTime}
          />
        </Col>

        <Col>
          <Select
            mode='tags'
            size={'middle'}
            placeholder={renderTranslation(language, TranslationKeys.outletSeasonTitle)}
            onChange={values => setSelectedSeasons(values)}
            style={{ width: '100%' }}
            options={seasonList.map(s => {
              return { value: s.id, label: s.name }
            })}
            status={statusSeason}
          ></Select>
        </Col>
        <Col>
          {typesOfChannels?.length > 1 ? (
            <Select
              size={'middle'}
              placeholder='Canale'
              value={selectedTypeOfChannel}
              onChange={values => setSelectedTypeOfChannel(values)}
              style={{ width: '100%' }}
              options={typesOfChannels}
            ></Select>
          ) : null}
        </Col>
        <Col>
          <Input
            placeholder={renderTranslation(language, TranslationKeys.logTableName)}
            onChange={e => setName(e.target.value)}
          />
        </Col>
        <Col className='d-flex align-center'>
          <Checkbox checked={showSubRules} onChange={e => setShowSubRules(e.target.checked)}>
            <span>{renderTranslation(language, TranslationKeys.detailedRules)}</span>
          </Checkbox>
        </Col>
        {/* <Col>
          <Select
            mode='tags'
            size={'middle'}
            placeholder={renderTranslation(language, TranslationKeys.transactionType)}
            onChange={values => setSelectedOperations(values)}
            style={{ width: '100%' }}
            options={operations}
          ></Select>
        </Col> */}
        <Col>
          <Button
            onClick={() => {
              getLogs()
            }}
          >
            {renderTranslation(language, TranslationKeys.applyFilter)}
          </Button>
        </Col>
        <Col>
          <Button
            onClick={() => {
              clearLogs()
            }}
          >
            {renderTranslation(language, TranslationKeys.clearFilter)}
          </Button>
        </Col>
        <Col span={24}>
          <Table
            id='table-omni-log'
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          ></Table>
        </Col>
      </Row>
    </Modal>
  )
}
